/**
 * Protected Route Wrapper
 *
 * Redirects unauthenticated users to the landing page with a redirect param.
 * Uses Auth0 authentication state.
 *
 * ## Usage
 *
 * Use in route layouts (recommended):
 * The _authenticated.tsx layout already handles this pattern.
 */

import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from './use-auth'
import type { ProtectedRouteProps } from './types'

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({
        to: '/',
        search: { redirect: window.location.pathname },
      })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="font-mono text-white text-2xl font-bold mb-2">
            ASH
          </div>
          <div className="font-mono text-neutral-400 text-sm animate-pulse">
            Loading...
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return <>{children}</>
}
